import { yupResolver } from '@hookform/resolvers/yup'
import {
  Button,
  Checkbox,
  DialogActions,
  FormControlLabel,
  FormGroup,
  Paper,
  Stack,
  Typography
} from '@mui/material'
import { FC, useCallback, useContext, useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'

import useAccessTokenAndShopId from '../../../hooks/useAccessTokenAndShopId/useAccessTokenAndShopId'
import { SubmitDialogContext } from '../../../providers/SubmitDialogProvider/SubmitDialogProvider'
import { useCreateLaborOverrideMutation } from '../../../redux/api'
import { LaborOverride } from '../../../sdk'
import { DEFAULT_LABOR_OVERRIDE } from '../defaultValues'
import { EditLaborDialogUI } from '../useEditLaborDialog/EditLaborDialogUI'
import { EditServiceForm } from '../useEditServiceDialog/EditServiceForm'
import { VALIDATE_LABOR_OVERRIDE } from '../validation'
import { AddServiceDialogContentWrapper } from './AddServiceDialogContentWrapper'
import { SearchServiceProps } from './LookupServiceWindow'

export const SearchCustom: FC<SearchServiceProps> = ({
  submitCallback,
  cancelCallback
}) => {
  const [createLaborOverride] = useCreateLaborOverrideMutation()
  const { accessToken } = useAccessTokenAndShopId()
  const { setIsSubmitting } = useContext(SubmitDialogContext)
  const [serviceSubmitted, setServiceSubmitted] = useState('')
  const [addLabor, setAddLabor] = useState(false)

  const methodsForLaborForm = useForm<LaborOverride>({
    mode: 'onBlur',
    defaultValues: {
      ...DEFAULT_LABOR_OVERRIDE
    },
    resolver: yupResolver(VALIDATE_LABOR_OVERRIDE)
  })
  const onSubmitLabor = useCallback(
    async (data: LaborOverride, serviceId: string) => {
      try {
        setIsSubmitting(true)
        await createLaborOverride({
          authorization: accessToken,
          createLaborOverridesRequest: {
            service: serviceId,
            laborOverrides: [
              {
                ...data,
                service: serviceId,
                hourlyRate: data.markup ? undefined : data.hourlyRate
              }
            ]
          }
        })
        submitCallback([serviceId])
      } catch (error) {
        setIsSubmitting(false)
        throw error
      }
    },
    [accessToken, createLaborOverride, submitCallback, setIsSubmitting]
  )
  const handleServiceSubmitted = useCallback(
    (serviceId: string) => {
      if (addLabor) {
        setServiceSubmitted(serviceId)
        methodsForLaborForm.handleSubmit((data) => {
          onSubmitLabor(data, serviceId)
        })()
      } else {
        submitCallback([serviceId])
      }
    },
    [addLabor, methodsForLaborForm, onSubmitLabor, submitCallback]
  )

  const onNameChange = useCallback(
    (serviceName: string) => {
      methodsForLaborForm.setValue('overrideName', `Labor - ${serviceName}`)
    },
    [methodsForLaborForm]
  )

  return (
    <>
      <AddServiceDialogContentWrapper>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={2}
          sx={{ width: '100%', overflowY: 'auto' }}>
          <Stack spacing={1} sx={{ width: '100%' }}>
            <EditServiceForm
              formId="customServiceForm"
              defaultValues={undefined}
              submitCallback={handleServiceSubmitted}
              disabled={!!serviceSubmitted}
              setSubmitting={setIsSubmitting}
              onNameChange={onNameChange}
            />
            <FormGroup>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={addLabor}
                    disabled={!!serviceSubmitted}
                    onChange={(event, checked) => {
                      setAddLabor(checked)
                    }}
                  />
                }
                label="Add custom labor"
              />
            </FormGroup>
          </Stack>

          {addLabor && (
            <Paper variant="outlined" sx={{ width: '100%', p: 2 }}>
              <Typography variant="subtitle1" paddingBottom={2}>
                Custom Labor
              </Typography>
              <FormProvider {...methodsForLaborForm}>
                <form id={'editLaborForm'}>
                  <EditLaborDialogUI
                    defaultValues={undefined}
                    defaultHour={0}
                    defaultName={undefined}
                  />
                </form>
              </FormProvider>
            </Paper>
          )}
        </Stack>
      </AddServiceDialogContentWrapper>
      <DialogActions>
        <Button onClick={() => cancelCallback()}>Cancel</Button>
        {serviceSubmitted ? (
          <Button
            variant="contained"
            onClick={() =>
              methodsForLaborForm.handleSubmit((data) =>
                onSubmitLabor(data, serviceSubmitted)
              )()
            }>
            Save
          </Button>
        ) : (
          <Button variant="contained" type="submit" form="customServiceForm">
            Save
          </Button>
        )}
      </DialogActions>
    </>
  )
}
